import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { OPEN_EMAIL_OFFER_EVENT } from "@/components/booking/booking-offer-popup";
import { useBookCta } from "@/hooks/use-book-href";

export interface PublicPromotion {
  id: string;
  type: string;
  title: string;
  terms: string;
  config?: {
    couponCode?: string;
    ctaHref?: string;
    ctaAction?: string;
    ctaLabel?: string;
  };
  sort_order: number;
}

interface CurrentPromotionsProps {
  embedded?: boolean;
  onSelect?: (promotion: PublicPromotion) => void;
  className?: string;
  /** Show only the offers and seasonal rates that apply to this home. */
  propertySlug?: string;
  /** Compact = denser list for booking sidebar */
  variant?: "default" | "compact";
  /** Start expanded (default: collapsed dropdown) */
  defaultOpen?: boolean;
}

function isEmailOfferCta(promo: PublicPromotion): boolean {
  const action = promo.config?.ctaAction;
  if (action === "email_offer") return true;
  // Back-compat for seeded email offer before ctaAction existed
  return promo.type === "display" && promo.config?.couponCode === "STAY5";
}

function promotionEyebrow(promo: PublicPromotion): string {
  switch (promo.type) {
    case "seasonal_rate":
      return "Seasonal rate";
    case "last_minute_rate":
      return "Last-minute";
    case "min_nights_schedule":
      return "Stay minimum";
    case "display":
      return isEmailOfferCta(promo) ? "Email offer" : "Offer";
    default:
      return "Offer";
  }
}

const promoCtaClass =
  "inline-flex items-center border border-ink text-ink px-3 py-1.5 mt-1 text-[12px] font-sans font-semibold uppercase tracking-[0.12em] hover:bg-ink hover:text-shell transition-colors";

/**
 * Collapsible public listing of current promotions.
 * Includes admin-managed offers plus current/upcoming seasonal nightly rates.
 */
export function CurrentPromotions({
  className,
  variant = "default",
  defaultOpen = false,
  propertySlug,
  embedded = false,
  onSelect,
}: CurrentPromotionsProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bookCta = useBookCta();
  const { data: promotions = [], isLoading } = useQuery<PublicPromotion[]>({
    queryKey: propertySlug ? ["/api/promotions", { property: propertySlug }] : ["/api/promotions"],
  });

  if (isLoading || promotions.length === 0) return null;

  const listing = (<ul className="space-y-0">
              {promotions.map((promo) => {
                const emailOffer = isEmailOfferCta(promo);
                const href =
                  (promo.config && typeof promo.config.ctaHref === "string" && promo.config.ctaHref) ||
                  bookCta.href;
                const ctaLabel =
                  (promo.config?.ctaLabel && String(promo.config.ctaLabel)) ||
                  (emailOffer ? "Get discount" : "Check dates");

                return (
                  <li
                    key={promo.id}
                    className="border-t border-sand-deep py-5 first:border-t-0"
                  >
                    <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-driftwood mb-1.5">
                      {promotionEyebrow(promo)}
                    </p>
                    <p className="font-serif font-normal text-forest text-[20px] md:text-[22px] leading-snug mb-2">
                      {promo.title}
                    </p>
                    <p className="font-sans text-[14px] md:text-[15px] text-ink-soft leading-relaxed max-w-[42rem] mb-3">
                      {promo.terms}
                    </p>
                    {onSelect && (emailOffer || promo.config?.couponCode || promo.type !== 'display' || !promo.config?.ctaHref || /^\/(?:properties\/[^/]+\/book|booking)(?:[?#]|$)/.test(href)) ? (
                      <button type="button" className={promoCtaClass} onClick={() => onSelect(promo)} data-testid={`promo-select-${promo.id}`}>{emailOffer ? ctaLabel : "Select offer"}</button>
                    ) : emailOffer ? (
                      <button
                        type="button"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent(OPEN_EMAIL_OFFER_EVENT));
                        }}
                        className={promoCtaClass}
                        data-testid={`promo-cta-email-${promo.id}`}
                      >
                        {ctaLabel}
                      </button>
                    ) : (
                      <Link
                        href={href}
                        className={promoCtaClass}
                        data-testid={`promo-cta-link-${promo.id}`}
                      >
                        {ctaLabel}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>);
  if (embedded) return listing;

  return (
    <section
      className={cn(
        variant === "compact" ? "py-2" : "py-3 md:py-4",
        className,
      )}
      aria-labelledby="current-promotions-heading"
      data-testid="current-promotions"
    >
      <div className="w-full">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger
            className={cn(
              "group flex w-full items-center justify-between gap-3 text-left",
              "border border-sun bg-sun px-4 py-2.5 min-h-11",
              "hover:bg-sun-hover transition-colors",
              open ? "rounded-t-sm rounded-b-none" : "rounded-sm",
            )}
            aria-expanded={open}
            data-testid="current-promotions-trigger"
          >
            <span className="min-w-0">
              <span
                id="current-promotions-heading"
                className={cn(
                  "block font-serif font-normal text-shell tracking-tight",
                  variant === "compact" ? "text-[18px]" : "text-[20px]",
                )}
              >
                Current promotions
              </span>
              <span className="block mt-0.5 font-sans text-[12px] md:text-[13px] text-shell/85 tracking-wide">
                {promotions.length} offer{promotions.length === 1 ? "" : "s"} available
              </span>
            </span>
            <ChevronDown
              className={cn(
                "h-5 w-5 shrink-0 text-shell transition-transform duration-200",
                open && "rotate-180",
              )}
              aria-hidden="true"
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            {listing}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </section>
  );
}
